import { Link } from "react-router-dom";
import { Avatar, Icon, IconButton, Typography } from "@/components/atoms";
import { ClientProfile } from "@/redux/apis/clientProfile/types";
import { getAssetUrl } from "@/utils";

interface YourDetailsProps {
  profile: ClientProfile;
  /** Route the edit pencil navigates to. Defaults to the self-service client route. */
  editTo?: string;
  /** Card heading. Pass `null` to suppress it (operator view supplies its own "Client Details" header). */
  heading?: string | null;
  /** Hide the edit affordance — e.g. an operator viewing a removed client (read-only). */
  readOnly?: boolean;
  /** Show VIP crown badge next to client name (operator context only). */
  isVip?: boolean;
}

const getInitials = (firstName: string, lastName: string) => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

const DetailValue = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <>
    <Typography variant="paragraph" className="truncate py-1 text-neutral-silver-gray">
      {label}
    </Typography>
    <Typography variant="action" className="py-1 text-right">
      {value || "-"}
    </Typography>
  </>
);

const ContactRow = ({ icon, label, value }: { icon: string; label: string; value: string | null | undefined }) => (
  <div className="flex items-center gap-3 overflow-hidden">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-neutral-gray">
      <Icon name={icon} size="sm" className="text-neutral-dark-gray" />
    </div>
    <div className="flex min-w-0 flex-col">
      <Typography variant="paragraph" className="text-neutral-silver-gray">
        {label}
      </Typography>
      <Typography variant="action" className="break-all">
        {value || "-"}
      </Typography>
    </div>
  </div>
);

export const YourDetails = ({
  profile,
  editTo = "/client/settings/profile",
  heading = "Your Details",
  readOnly = false,
  isVip = false,
}: YourDetailsProps) => {
  const { personal, company, preferences, avatar } = profile;
  const fullName = `${personal.first_name} ${personal.last_name}`.trim();
  const avatarUrl = avatar ? getAssetUrl(avatar, { height: 160, width: 160, fit: "cover" }) : undefined;

  return (
    <div>
      {heading != null && (
        <Typography variant="h2" className="mb-4">
          {heading}
        </Typography>
      )}
      <div className="rounded-lg border border-neutral-mid-gray bg-white @container">
        <div className="grid grid-cols-1 @[700px]:grid-cols-[5fr_1px_7fr]">
          {/* Left Column — Personal Info */}
          <div className="flex flex-col gap-5 p-5">
            <div className="flex items-center gap-4">
              <Avatar size="xl" src={avatarUrl} alt={fullName} className="h-12 w-12 bg-neutral-mid-gray @[700px]:h-[72px] @[700px]:w-[72px]">
                {!avatar && (
                  <Typography variant="h3" className="text-neutral-dark-gray">
                    {getInitials(personal.first_name, personal.last_name)}
                  </Typography>
                )}
              </Avatar>
              <div>
                <Typography variant="paragraph" className="text-neutral-silver-gray">
                  Passenger
                </Typography>
                <div className="flex items-center gap-1.5">
                  <Typography variant="h2">{fullName}</Typography>
                  {isVip && <Icon name="Crown" variant="Bold" size="sm" className="text-warning" />}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <ContactRow icon="Sms" label="Email address" value={personal.email} />
              <ContactRow icon="Call" label="Mobile no." value={personal.mobile} />
              <ContactRow icon="Building" label="Company" value={company.company_name} />
            </div>
          </div>

          {/* Vertical Divider */}
          <div className="hidden py-5 @[700px]:block">
            <div className="h-full border-r border-neutral-mid-gray" />
          </div>

          {/* Horizontal Divider — shown when stacked */}
          <div className="mx-5 border-b border-neutral-mid-gray @[700px]:hidden" />

          {/* Right Column — Preferences */}
          <div className="grid grid-cols-[auto_auto_1fr] items-baseline gap-x-[44px] overflow-hidden p-5">
            <DetailValue label="Seating Preference" value={preferences.seating} />
            {!readOnly && (
              <Link to={editTo} className="row-span-5 self-start justify-self-end">
                <IconButton iconName="Edit2" variant="primary" size="sm" />
              </Link>
            )}

            <DetailValue label="Radio Preference" value={preferences.radio} />
            <DetailValue label="Quiet Ride" value={preferences.quiet_ride} />
            <DetailValue label="Vehicle Temp" value={preferences.vehicle_temp ? `${preferences.vehicle_temp}° C` : undefined} />
            <DetailValue
              label="Airport Waiting Time Preference"
              value={
                preferences.airport_waiting_time
                  ? `${preferences.airport_waiting_time} ${preferences.airport_waiting_timeframe === "minute" ? "min" : "hour"}${
                      Number(preferences.airport_waiting_time) !== 1 ? "s" : ""
                    }`
                  : undefined
              }
            />

            <div className="col-span-full flex flex-col py-1">
              <Typography variant="paragraph" className="text-neutral-silver-gray">
                Notes
              </Typography>
              <Typography variant="action">{preferences.driver_notes || "-"}</Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
